import { memo, useState } from "react";
import { oneClickToggle, useSettings } from "@helm/core";
import { oneClickStatusLabel } from "@helm/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { settingsSync } from "../session";

/** The one explanation (SPEC §9.8), shared with Settings → One-click trading. */
export function OneClickExplanation(): React.JSX.Element {
  return (
    <p>
      Orders from the DOM, the order pad, chart trading and trading hotkeys will be sent immediately, without
      the confirmation popover. Exit All has its own preference in Settings (Ask before Exit All). Every
      gateway check (risk limits, your per-account max, the READY session, the trading switch) still applies.
      Turn it off again with one click on the 1-click switch or in Settings.
    </p>
  );
}

/**
 * One-click trading toggle (owner 2026-09-28 "dom needs to easily be able to remove the
 * confirmation"; TradingView / Tradovate style). Since the 2026-10-04 UI clean-up it lives once for
 * the window, in the desk's status line ("1-click on" / "1-click off"; `statusLine`), plus on a DOM
 * outside the dock. A badge that says which mode is on — amber "1-CLICK ON" (orders go at once) or
 * "CONFIRM" (each order is confirmed in words) — and flips it with one click. Turning one-click
 * **on** shows the SPEC §9.8 explanation the first time only (`one_click_explained`); off is
 * always one click. Persisted per user in the synced settings (`oneClick`), so every DOM, pad and
 * device follows. Only the client confirm popover is affected: the gateway's checks are unchanged.
 */
export const OneClickToggle = memo(function OneClickToggle({
  compact = false,
  statusLine = false,
}: {
  compact?: boolean;
  /** The status-line form: "1-click on" / "1-click off" (same switch, same first-time explanation). */
  statusLine?: boolean;
}): React.JSX.Element {
  const on = useSettings((s) => s.settings.oneClick);
  const explained = useSettings((s) => s.settings.one_click_explained);
  const [explain, setExplain] = useState(false);
  const flip = (): void => {
    const next = oneClickToggle({ oneClick: on, one_click_explained: explained });
    if (next.explain) setExplain(true);
    else void settingsSync.update(next.patch);
  };
  const label = statusLine
    ? oneClickStatusLabel(on)
    : on
      ? compact
        ? "1-CLICK"
        : "1-CLICK ON"
      : compact
        ? "CONFIRM"
        : "CONFIRM ON";
  return (
    <>
      <button
        type="button"
        className={`oneclick-badge${on ? " oneclick-on" : ""}${statusLine ? " oneclick-statusline" : ""}`}
        role="switch"
        aria-checked={on}
        aria-label={on ? "One-click trading is on" : "One-click trading is off (orders are confirmed)"}
        data-oneclick={on ? "on" : "off"}
        title={
          on
            ? "One-click trading ON: orders from the DOM, pad, chart and hotkeys go at once. Click to confirm each order again."
            : "Each order is confirmed in words before it is sent. Click to turn on one-click trading."
        }
        onClick={flip}
      >
        <span className="oneclick-dot" aria-hidden="true" />
        {label}
      </button>
      {explain && (
        <ConfirmDialog
          title="Turn on one-click trading?"
          confirmLabel="Turn on"
          onCancel={() => {
            setExplain(false);
          }}
          onConfirm={() => {
            setExplain(false);
            void settingsSync.update({ oneClick: true, one_click_explained: true });
          }}
        >
          <OneClickExplanation />
        </ConfirmDialog>
      )}
    </>
  );
});
